import React, { useEffect, useState, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import * as THREE from "three";
import { toast } from "react-toastify";
import '../../../style/newfileupload.css';

export interface ExistingFile {
  name: string;
  url: string;
}

interface ModelViewerProps {
  files: (File | ExistingFile)[];
  onDelete: (index: number) => void;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  // onSave: (scanFiles: (File | ExistingFile)[]) => void;
  onSave: (data: {
    files: (File | ExistingFile)[];
    fileUrl: string;
  }) => void;
}

interface FileObjectPair {
  file: File | ExistingFile;
  object: THREE.Object3D | null;
  isLoading: boolean;
  isImage: boolean;
  imageUrl?: string;
  visible: boolean;
}
interface NewFileUploadProps {
  scans: (File | ExistingFile)[];
  setScans: React.Dispatch<React.SetStateAction<(File | ExistingFile)[]>>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}

const UPPER_COLOR = "#dbe6dcff";
const LOWER_COLOR = "#dbe6dcff";

const FitCamera: React.FC<{ objects: THREE.Object3D[] }> = ({ objects }) => {
  const { camera, controls } = useThree();

  useEffect(() => {
    if (!objects.length) return;

    const box = new THREE.Box3();
    objects.forEach((obj) => box.expandByObject(obj));

    const center = new THREE.Vector3();
    box.getCenter(center);

    const size = new THREE.Vector3();
    box.getSize(size);

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

    camera.position.set(center.x, center.y, cameraZ * 1.5);
    camera.lookAt(center);
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();

    if (controls) {
      (controls as any).target.copy(center);
      (controls as any).update();
    }
  }, []);

  return null;
};

const ModelViewer: React.FC<ModelViewerProps> = ({ files, onDelete, setActiveStep, onSave }) => {
  const [fileObjects, setFileObjects] = useState<FileObjectPair[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasUploadedFiles, setHasUploadedFiles] = useState<boolean>(false);
  const [gapOffset, setGapOffset] = useState<number>(0);
  //const [fileUrl, setFileUrl] = useState("");
  const [fileUrl, setFileUrl] = useState(
    () => sessionStorage.getItem("fileUrl") || ""
  );
  const validFiles = useMemo(() => {
    return files.filter(file => {
      const fileName = file instanceof File ? file.name : file.name;
      const ext = fileName.toLowerCase().split('.').pop();
      return ext === 'stl' || ext === 'obj';
    });
  }, [files]);

  // useEffect(() => {
  //   if (files.length > 0 && validFiles.length === 0) {
  //     setErrorMessage("Please upload only STL or OBJ files. Other file types are not supported.");
  //   } else {
  //     setErrorMessage("");
  //   }
  // }, [files, validFiles]);


  useEffect(() => {
    if (files.length > 0 && validFiles.length === 0) {
      setErrorMessage("Please upload only STL or OBJ files. Other file types are not supported.");
    } else {
      setErrorMessage("");
    }


    if (validFiles.length > 0) {
      setHasUploadedFiles(true);
    } else {
      setHasUploadedFiles(false);
    }
  }, [files, validFiles]);

  useEffect(() => {
    if (!validFiles || validFiles.length === 0) {
      setFileObjects([]);
      return;
    }

    const initialFileObjects: FileObjectPair[] = validFiles.map((file) => ({
      file,
      object: null,
      isLoading: true,
      isImage: false,
      visible: true,
    }));

    setFileObjects(initialFileObjects);

    const loaders = {
      stl: new STLLoader(),
      obj: new OBJLoader(),
    };





    const load3DModel = (
      data: ArrayBuffer | string,
      fileName: string,
      idx: number
    ) => {
      let object: THREE.Object3D | null = null;
      try {
        if (fileName.toLowerCase().endsWith(".stl") && data instanceof ArrayBuffer) {
          const geometry = loaders.stl.parse(data);
          geometry.computeVertexNormals();
          geometry.computeBoundingBox();
          const box = geometry.boundingBox!;
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const scale = 5 / Math.max(size.x, size.y, size.z);

          geometry.translate(-center.x, -center.y, -center.z);
          geometry.scale(scale, scale, scale);

          object = new THREE.Mesh(
            geometry,
            new THREE.MeshStandardMaterial({ color: "#e0e0e0" })
          );
          object.rotation.x = -Math.PI / 2;
          object.rotation.z = Math.PI;
          (object as any).fileName = fileName;

        } else if (fileName.toLowerCase().endsWith(".obj") && typeof data === "string") {
          object = loaders.obj.parse(data);

          object.traverse((child: any) => {
            if (child.isMesh)
              child.material = new THREE.MeshStandardMaterial({
                color: "#e0e0e0",
              });
          });

          const box = new THREE.Box3().setFromObject(object);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const scale = 5 / Math.max(size.x, size.y, size.z);

          object.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
          object.scale.set(scale, scale, scale);

          (object as any).fileName = fileName;
        }
      } catch (err) {
        console.error("Error parsing file:", err);

        let errorMessage = "Unknown error occurred";
        if (err instanceof Error) {
          errorMessage = err.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }
        setErrorMessage(`Error loading file ${fileName}: ${errorMessage}`);
      }

      setFileObjects((prev) =>
        prev.map((f, i) =>
          i === idx ? { ...f, object, isLoading: false } : f
        )
      );
    };

    validFiles.forEach((file, idx) => {
      const fileName = file instanceof File ? file.name : file.name;

      if (file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const contents = e.target?.result;
          if (!contents) return;
          load3DModel(contents, fileName, idx);
        };
        if (fileName.toLowerCase().endsWith(".stl"))
          reader.readAsArrayBuffer(file);
        else if (fileName.toLowerCase().endsWith(".obj"))
          reader.readAsText(file);
      } else {
        const fileUrl = (file as ExistingFile).url;
        fetch(fileUrl)
          .then(async (res) => {
            let data: ArrayBuffer | string;
            if (fileUrl.toLowerCase().endsWith(".stl")) {
              data = await res.arrayBuffer();
            } else {
              data = await res.text();
            }
            return data;
          })
          .then((data: ArrayBuffer | string) => {
            load3DModel(data, fileName, idx);
          })
          .catch((error) => {
            console.error("Error fetching file:", error);
            let errorMessage = "Failed to fetch file";
            if (error instanceof Error) {
              errorMessage = error.message;
            } else if (typeof error === "string") {
              errorMessage = error;
            }
            setErrorMessage(`Error loading file ${fileName}: ${errorMessage}`);
            setFileObjects((prev) =>
              prev.map((f, i) =>
                i === idx ? { ...f, isLoading: false } : f
              )
            );
          });
      }
    });
  }, [validFiles]);



  useEffect(() => {
    const loaded = fileObjects.filter(f => f.object && !f.isLoading);

    if (loaded.length === 0) return;

    // Separate upper and lower models
    const upperModels = loaded.filter(fileObj => {
      const fileName = ((fileObj.object as any).fileName || '').toLowerCase();
      return fileName.includes("upper") || fileName.includes("maxillary");
    });

    const lowerModels = loaded.filter(fileObj => {
      const fileName = ((fileObj.object as any).fileName || '').toLowerCase();
      return fileName.includes("lower") || fileName.includes("mandibular");
    });

    // Calculate bounding boxes for each model
    const getModelBounds = (obj: THREE.Object3D) => {
      const box = new THREE.Box3().setFromObject(obj);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      return { box, size, center };
    };

    // Dynamic gap calculation
    let baseGap = 0;

    if (upperModels.length > 0 && lowerModels.length > 0) {
      const upperHeights = upperModels.map(f => getModelBounds(f.object!).size.y);
      const lowerHeights = lowerModels.map(f => getModelBounds(f.object!).size.y);

      const maxUpperHeight = Math.max(...upperHeights);
      const maxLowerHeight = Math.max(...lowerHeights);

      baseGap = (maxUpperHeight + maxLowerHeight) * 0.2;
    }
    else if (upperModels.length > 0 || lowerModels.length > 0) {
      const singleModel = upperModels.length > 0 ? upperModels[0] : lowerModels[0];
      const { size } = getModelBounds(singleModel.object!);
      baseGap = size.y * 0.4;
    }

    // Final gap = baseGap + manual offset
    const gap = Math.max(0, baseGap + gapOffset);


    // Position upper models
    upperModels.forEach((fileObj) => {
      const obj = fileObj.object!;
      obj.rotation.set(0, 0, 0);
      obj.position.y = gap / 2;
      obj.traverse((child: any) => {
        if (child.isMesh)
          child.material.color.set(UPPER_COLOR);
      });
    });

    // Position lower models
    lowerModels.forEach((fileObj) => {
      const obj = fileObj.object!;
      obj.rotation.set(0, 0, 0);
      obj.position.y = -gap / 2;
      obj.traverse((child: any) => {
        if (child.isMesh)
          child.material.color.set(LOWER_COLOR);
      });
    });

  }, [fileObjects, gapOffset]);

  const toggleVisibility = (index: number) => {
    setFileObjects(prev =>
      prev.map((f, i) =>
        i === index ? { ...f, visible: !f.visible } : f
      )
    );
  };

  const visibleObjects = useMemo(() => {
    return fileObjects
      .filter(f => f.visible && f.object && !f.isLoading)
      .map(f => f.object) as THREE.Object3D[];
  }, [fileObjects]);

  const deleteFile = (index: number) => {
    const originalIndex = files.findIndex((file, i) => {
      const fileName = file instanceof File ? file.name : file.name;
      const validFileName = validFiles[index] instanceof File
        ? (validFiles[index] as File).name
        : (validFiles[index] as ExistingFile).name;
      return fileName === validFileName;
    });

    if (originalIndex !== -1) {
      onDelete(originalIndex);
    }
  };

  const handleContinue = () => {
  const selectedFiles = fileObjects
    .filter(f => f.visible)
    .map(f => f.file);

  if (selectedFiles.length === 0) {
    toast.info("Please upload at least one scan file");
    return;
  }

  onSave({
    files: selectedFiles,
    fileUrl
  });

  const serviceType = sessionStorage.getItem("serviceType");

  if (serviceType === "design") {
    setActiveStep(3);
  } else {
    setActiveStep(4);
  }
};

  return (
    <div className="">
      <div className="">
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}


        <div className="row mt-1">

          <div className="col-md-8">


            <div className="viewer-canvas">
              {hasUploadedFiles && (
                <ul className="btn-plus-and-minus">
                  <li>
                    <div className="gap-label">Adjust Bite Gap</div>
                  </li>
                  <li>
                    <div className="gap-buttons">
                      <button
                        onClick={() => setGapOffset(prev => prev - 0.1)}
                        className="gap-btn"
                      >
                        -
                      </button>

                      <span className="gap-value">
                        {gapOffset.toFixed(2)}
                      </span>

                      <button
                        onClick={() => setGapOffset(prev => prev + 0.1)}
                        className="gap-btn"
                      >
                        +
                      </button>
                    </div>
                  </li>
                </ul>
              )}


              <Canvas
                camera={{ position: [1, 1, 1], fov: 50 }}
                style={{ background: hasUploadedFiles ? '#000000' : '#F8F9FB' }}
              >
                <color attach="background" args={[hasUploadedFiles ? '#000000' : '#F8F9FB']} />

                <ambientLight intensity={0.5} />
                <directionalLight position={[5, 10, 5]} intensity={1.2} />
                <pointLight position={[-10, -10, -5]} intensity={0.3} />
                <OrbitControls enableDamping dampingFactor={0.05} minDistance={0} maxDistance={50} />

                {visibleObjects.length > 0 && <FitCamera objects={visibleObjects} />}

                {fileObjects.map((fileObj, idx) => {
                  if (!fileObj.visible || !fileObj.object) return null;
                  return <primitive key={`object-${idx}`} object={fileObj.object} />;
                })}

              </Canvas>


              {validFiles.length === 0 && !errorMessage && (
                <div className="empty-preview">
                  <p>Drag & drop</p>
                  <p>or</p>
                  <div className="file-input-section-whichwillgoinside">
                    <div className="customfileupload">
                      <label htmlFor="fileUpload" className="upload-btn">


                        <img alt="Logo" src="../../flaticon/AddScan.png" ></img>
                        <span className="upload-scan-text">Add Scan (s)</span><br />
                      </label>

                    </div></div>
                  <p className="upload-scan-text"></p>

                  <p className="st-and-objfiles">(.stl & .obj files only)</p>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-md-12 mt-5">
                <div className="share-direcly">
                  <input
                    className="option-checkbox"
                    type="checkbox"
                  />
                  <div className="share-text">
                    Shared Directly with us? Add note with the file name/link below.
                  </div>
                </div>

                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Scanner Name/File URL"
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4 sidebar-section-position">
            <div className="viewer-sidebar ">
              {fileObjects.length > 0 && (
                <div className="file-card-group">
                  <div className="file-header">
                    <span className="file-title">
                      {fileObjects[0].file instanceof File
                        ? fileObjects[0].file.name
                        : fileObjects[0].file.name}
                    </span>
                    <button
                      className="delete-button"
                      onClick={() => {
                        const indicesToDelete: number[] = [];
                        fileObjects.forEach((fileObj, index) => {
                          if (fileObj.visible) {
                            indicesToDelete.push(index);
                          }
                        });

                        for (let i = indicesToDelete.length - 1; i >= 0; i--) {
                          deleteFile(indicesToDelete[i]);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>

                  <div className="file-options">
                    {fileObjects.map((fileObj, index) => {
                      const fileName = fileObj.file instanceof File ? fileObj.file.name : fileObj.file.name;
                      const isUpper = fileName.toLowerCase().includes('upper');
                      const isLower = fileName.toLowerCase().includes('lower');

                      return (
                        <div key={index} className="option-row">
                          <span className="option-label">
                            {isUpper ? 'Upper' : isLower ? 'Lower' : fileName}
                          </span>
                          <input
                            type="checkbox"
                            checked={fileObj.visible}
                            onChange={() => toggleVisibility(index)}
                            className="option-checkbox"
                          />
                        </div>
                      );
                    })}
                  </div>

                  {fileObjects.some(f => f.isLoading) && (
                    <div className="loading-indicator">Loading 3D models...</div>
                  )}
                </div>
              )}

              {/* Info Box */}
              <div className="info-box-and-btn">
                <div className="info-box">

                  <div className="info-content">

                    <div className="please-ens">
                      <p className="info-title"> Please ensure:</p></div>
                    <div className="list and-icon-list">
                      <ul className="info-list">
                        <li>Preview before submitting</li>
                        <li>Ensure Clear margins & full prep visible</li>
                        <li>Accurate bite alignment</li>
                        <li>No missing or distorted areas</li>
                        <li>Use supported file formats (.stl, .obj)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <div className="sidebar-footer2 mt-3">
                  <button className="continue-btn" onClick={handleContinue}>Continue</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



    </div>
  );
};


const NewFileUpload: React.FC<NewFileUploadProps> = ({ scans, setScans, setActiveStep }) => {
  //const [files, setFiles] = useState<(File | ExistingFile)[]>(scans);
  //const [isFileSelected, setIsFileSelected] = useState(false);



  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const newFiles = Array.from(fileList);

    const validFiles = newFiles.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ext === 'stl' || ext === 'obj';
    });

    if (validFiles.length !== newFiles.length) {
      toast.warning("Only STL and OBJ files are allowed. Other file types have been ignored.");
    }

    setScans(prev => [...prev, ...validFiles]);
    //setIsFileSelected(true);
    e.target.value = "";
  };

  const deleteFile = (index: number) => {
    setScans(prev => prev.filter((_, i) => i !== index));

  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles) return;

    const newFiles = Array.from(droppedFiles);

    const validFiles = newFiles.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ext === 'stl' || ext === 'obj';
    });

    if (validFiles.length !== newFiles.length) {
      toast.warning("Only STL and OBJ files are allowed. Other file types have been ignored.");
    }

    setScans(prev => [...prev, ...validFiles]);
    // setIsFileSelected(true);
  };



  return (
    <div className="case-details2">
      <div className="tab-content whole-tab-content mt-4">
        <div className="tab-pane fade show active">
          <div className="row">
            <div className="col-md-12">
              <input
                id="fileUpload"
                type="file"
                multiple
                accept=".stl,.obj"
                onChange={handleFileInput}
                hidden
              />
            </div>
          </div>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ width: "100%" }}
          >
            {/* <ModelViewer
              files={scans}
              onDelete={(index) =>
                setScans(prev => prev.filter((_, i) => i !== index))
              }
              setActiveStep={setActiveStep}
              onSave={(files) => setScans(files)}
            /> */}
            <ModelViewer
              files={scans}
              onDelete={(index) =>
                setScans(prev => prev.filter((_, i) => i !== index))
              }
              setActiveStep={setActiveStep}
              onSave={({ files, fileUrl }) => {
                setScans(files);

                // 👉 store fileUrl somewhere (important)
                sessionStorage.setItem("fileUrl", fileUrl);
              }}
            />
          </div>


        </div>
      </div>

      <style>{`

        .back-btndiv {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .back-text {
          font-size: 19px;
          color: #333;
        }
        .tab-pane {
          display: none;
        }

        .tab-pane.show {
          display: block;
        }

        .col-md-3 {
          flex: 0 0 25%;
          max-width: 25%;
        }
   
.info-box-and-btn {
   position: absolute;
    width: 90%;
    bottom: 0;
}
        .back-btndiv {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .back-text {
          font-size: 19px;
          color: #333;
        }
      
        .tab-content {
          padding-top: 20px;
        }

        .tab-pane {
          display: none;
        }

        .tab-pane.show {
          display: block;
        }
     
.sidebar-footer2 {
    margin: 1px 16px;
    padding-bottom:10px;
}
        .error-message {
          position: absolute;
          top: -98px;
          // left: 87%;
    left: 64%;
          transform: translateX(-50%);
          background: #dc3545;
          color: white;
          padding: 10px 20px;
          border-radius: 4px;
         z-index: 997;
          font-size: 14px;
          animation: fadeIn 0.3s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
.viewer-canvas {
   flex: 1;
    position: relative;
    max-width: 560px;
    height: 460px;
    background-color: #F8F9FB;
    font-weight: 500;
    transition: background-color 0.3s ease;
    border: 1px dashed #ADB5BD;
    border-radius: 5px;
    /* margin-left: auto; */
    /* margin-right: auto; */
    margin: auto;
}


        .viewer-canvas:has(canvas[style*="background: #000000" ,  border-radius: 8px;]) {
  background-color: #000000;
  
}


.viewer-canvas:has(canvas[style*="background: #000000"]) .empty-preview {
  color: #ffffff;
}

        .empty-preview {
          position: absolute;
          top: 50%;
              left: 55%;
          transform: translate(-65%, -50%);
          text-align: center;
          color: #999;
        }

        .empty-preview p {
          margin: 8px 0;
           font-weight: 400;
               font-size: 15px;
        }
       .viewer-sidebar {

    padding: 16px 5px;

  
    width: 100%;
  
   
        }

        .file-card-group {
          padding: 16px;
    
        
        }

        .file-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .file-title {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          flex: 1;
          word-break: break-word;
          padding-right: 8px;
        }

        .delete-button {
          background: #dc3545;
          color: #fff;
          
          border: 1px solid #dc3545;
          border-radius: 4px;
          padding: 4px 12px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .delete-button:hover {
          background: #dc3545;
          color: #fff;
        }

        .file-options {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .option-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          border: 1px solid #f0f0f0;
        }

        .option-label {
          font-size: 14px;
          color: #666;
        }

        .option-checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color:#0D6EFD;
        }

        .loading-indicator {
          font-size: 12px;
          color: #999;
          font-style: italic;
          margin-top: 8px;
        }

        .info-box {
          flex: 1;
       
        
          margin:1px 16px;
          display: flex;
          gap: 10px;
          position:relative;
        }

        .info-icon {
          font-size: 14px;
          color: #1976d2;
          font-weight: bold;
          flex-shrink: 0;
          margin-right:10px;
        }

        .info-content {
        position: absolute;
    bottom: 0;
    width: 100%;
    background-color:#E7F1FF;
    padding: 5px 15px;
    border-radius: 4px;
        }

        .info-title {
        display:flex;
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin: 0 0 8px 0;
          padding-top:10px;
        }

        .info-list {
          margin: 0;
          padding-left: 15px;

          font-size: 13px;
          color: #555;
        }

        .info-list li {
          margin-bottom: 4px;
         
        }

        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid #e0e0e0;
        }

        .continue-btn-sidebar {
          width: 27%;
          padding: 12px;
           margin-left: auto;
          background: #2196F3;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .continue-btn-sidebar:hover {
          background: #1976d2;
          box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
        }

        .continue-btn-sidebar:active {
          transform: translateY(1px);
        }

      
      `}</style>
    </div>
  );
};

export default NewFileUpload; 